import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type {
  Beacon,
  MeasurementPoint,
  PlanPoint,
  ProviderRating,
  RadioTechnology,
} from '@oscom/shared';
import { RatingMatrix } from './RatingMatrix';

export type PlanMarkerInfoContent =
  | {
      kind: 'point';
      point: MeasurementPoint;
      ratings: ProviderRating[];
      technologies: readonly RadioTechnology[];
      providers: readonly string[];
    }
  | { kind: 'beacon'; beacon: Beacon };

export interface PlanMarkerInfoProps {
  /** Marker position in container coordinates. */
  screen: PlanPoint;
  content: PlanMarkerInfoContent;
  onClose: () => void;
  /** Extra buttons below the data (edit mode). */
  actions?: ReactNode;
}

const GAP_PX = 16;
const EDGE_PX = 8;

const orDash = (value: string | undefined): string =>
  value === undefined || value === '' ? '---' : value;

/**
 * Popover at a marker: the values of a measurement point or the data of a
 * beacon. Closes with Escape and with a press anywhere outside it (the canvas
 * closes it on every view change), then returns focus to the opener.
 */
export function PlanMarkerInfo({
  screen,
  content,
  onClose,
  actions,
}: PlanMarkerInfoProps): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState<PlanPoint>({ x: screen.x, y: screen.y + GAP_PX });

  // Below the marker when it fits, otherwise above; never past the side edges.
  useLayoutEffect(() => {
    const element = ref.current;
    const parent = element?.offsetParent;
    if (!element || !(parent instanceof HTMLElement)) return;
    const width = element.offsetWidth;
    const height = element.offsetHeight;
    const below = screen.y + GAP_PX;
    const y =
      below + height <= parent.clientHeight || screen.y - GAP_PX - height < 0
        ? below
        : screen.y - GAP_PX - height;
    const x = Math.max(
      EDGE_PX,
      Math.min(screen.x - width / 2, parent.clientWidth - width - EDGE_PX),
    );
    setOffset({ x, y });
  }, [screen.x, screen.y, content]);

  // Latest callback without re-running the effect (which would steal focus).
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  }, [onClose]);

  useEffect(() => {
    // Usually the marker that opened the popover; focus goes back there.
    const opener = document.activeElement;
    const popover = ref.current;
    // When the opener is gone (marker taken off the plan): the plan surface.
    const surface = popover?.parentElement;
    popover?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close.current();
    };
    const onPress = (event: Event): void => {
      if (event.target instanceof Node && popover?.contains(event.target)) return;
      close.current();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPress);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPress);
      // Only when focus went down with the popover, never away from a control
      // the user has moved to in the meantime.
      const active = document.activeElement;
      const focusLost = active === null || active === document.body || popover?.contains(active);
      if (!focusLost) return;
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
      else if (surface?.isConnected) surface.focus();
    };
  }, []);

  const title =
    content.kind === 'point' ? `Punkt ${content.point.sequenceNumber}` : content.beacon.beaconName;

  return (
    <div
      ref={ref}
      className="plan-info"
      role="dialog"
      aria-label={title}
      tabIndex={-1}
      data-plan-overlay=""
      style={{ left: `${offset.x}px`, top: `${offset.y}px` }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div className="plan-info__head">
        <h2 className="plan-info__title">{title}</h2>
        <button type="button" className="button button--ghost" onClick={onClose}>
          Schließen
        </button>
      </div>
      {content.kind === 'point' ? (
        <>
          <dl className="plan-info__data">
            <dt>Nr.</dt>
            <dd>{content.point.sequenceNumber}</dd>
            <dt>Ortsfeld</dt>
            <dd>{orDash(content.point.locationNote)}</dd>
          </dl>
          <RatingMatrix
            ratings={content.ratings}
            technologies={content.technologies}
            providers={content.providers}
          />
        </>
      ) : (
        <dl className="plan-info__data">
          <dt>Beacon Name</dt>
          <dd>{content.beacon.beaconName}</dd>
          <dt>Position</dt>
          <dd>{orDash(content.beacon.positionDescription)}</dd>
          <dt>Seriennummer</dt>
          <dd>{orDash(content.beacon.hardware?.serialNumber)}</dd>
          <dt>MAC</dt>
          <dd>{orDash(content.beacon.hardware?.macAddress)}</dd>
        </dl>
      )}
      {actions ? <div className="plan-info__actions">{actions}</div> : null}
    </div>
  );
}
